import React, {useState} from 'react'
import gql from 'graphql-tag'
import {useQuery} from '@apollo/react-hooks'
import {Button, Table} from 'antd'
import {PaperClipOutlined} from '@ant-design/icons'
import t from 'lib/translations'
import {formatPrice} from 'lib/util'
import AddInvoiceLineModal from './AddInvoiceLineModal'
import Loader from 'shared/components/Loader'
import DeleteButton from './DeleteButton'
import './index.css'

interface Props {
  bookingId: string
}

interface ServiceProviderInvoiceLine {
  id: string
  description: string
  quantity: number
  unitPrice: number
  state: string
  attachments: [string]
  isExcluded: boolean
  canDelete: boolean
}

interface Booking {
  serviceProviderInvoiceLines: [ServiceProviderInvoiceLine]
  serviceProviderInvoiceLinesSum: number
}

const inviceLinesQuery = gql`
  query ServiceProviderInvoiceLinesQuery($bookingId: ID!) {
    booking(id: $bookingId) {
      id
      serviceProviderInvoiceLines {
        id
        state
        description
        quantity
        unitPrice
        attachments
        canDelete
        isExcluded
      }
      serviceProviderInvoiceLinesSum
      availableExpenseItems {
        itemNo
        description
      }
    }
  }
`

function InvoiceLines({ bookingId }: Props) {
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const {data, loading, error, refetch} = useQuery(inviceLinesQuery, {
    variables: {bookingId},
    // Use cache-and-network so loading gets updated when refetching
    fetchPolicy: 'cache-and-network',
  })

  if (loading || error) {
    return <Loader loading={loading} error={error} />
  }

  const {
    serviceProviderInvoiceLines,
    serviceProviderInvoiceLinesSum,
  } = data.booking as Booking

  return (
    <div className="invoice-lines">
      <Table
        size="small"
        pagination={false}
        scroll={{x: true}}
        columns={[
          {key: 'description', dataIndex: 'description', title: 'Beskrivelse'},
          {key: 'quantity', dataIndex: 'quantity', title: 'Antall'},
          {key: 'unitPrice', dataIndex: 'unitPrice', title: 'Enhetspris'},
          {key: 'state', dataIndex: 'state', title: 'Tilstand'},
          {key: 'attachments', dataIndex: 'attachments', title: 'Vedlegg'},
          {key: 'sum', dataIndex: 'sum', title: 'Sum', align: 'right', fixed: 'right', width: 100},
          {key: 'actions', dataIndex: 'actions', title: '', fixed: 'right', width: 50},
        ]}
        dataSource={[
          ...serviceProviderInvoiceLines.map((line) => ({
            key: line.id,
            description: line.description,
            quantity: line.quantity,
            unitPrice: formatPrice(line.unitPrice),
            state: t(`invoiceLineState.${line.state}`),
            attachments: (
              <>
                {line.attachments.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Åpne vedlegg"
                  >
                    <PaperClipOutlined />
                  </a>
                ))}
              </>
            ),
            sum: (
              <span className={`sum${line.isExcluded ? ' excluded' : ''}`}>
                {formatPrice(line.quantity * line.unitPrice)}
              </span>
            ),
            actions: (
              <>
                {line.canDelete && (
                  <DeleteButton
                    bookingId={bookingId}
                    invoiceLineId={line.id}
                    refetch={refetch}
                  />
                )}
              </>
            ),
          })),
          {
            key: 'sum',
            description: 'Sum total',
            quantity: '',
            unitPrice: '',
            state: '',
            attachments: '',
            sum: formatPrice(serviceProviderInvoiceLinesSum),
            actions: '',
          },
        ]}
      />

      <div className="actions">
        <Button
          type="primary"
          onClick={() => setModalIsOpen(true)}
        >
          Legg til fakturalinje
        </Button>
      </div>

      <AddInvoiceLineModal
        bookingId={bookingId}
        availableExpenseItems={data.booking.availableExpenseItems}
        open={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        onInvoiceLine={() => {
          refetch()
          setModalIsOpen(false)
        }}
      />
    </div>
  )
}

export default InvoiceLines
